if (!customElements.get('rt-sticky-purchase')) {
  customElements.define('rt-sticky-purchase', class extends HTMLElement {
    connectedCallback() {
      this.hidden = true;
      this.setAttribute('data-enhanced', '');
      this.abort = new AbortController();
      this.link = this.querySelector('[data-purchase-link]');
      this.button = this.querySelector('[data-purchase-add]');
      this.price = this.querySelector('[data-purchase-price]');
      if (!this.link || !this.button) return;

      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };
      const options = { signal: this.abort.signal };
      window.addEventListener('scroll', this.schedule, { ...options, passive: true });
      window.addEventListener('resize', this.schedule, options);
      window.visualViewport?.addEventListener('resize', this.schedule, options);
      document.addEventListener('shopify:section:load', this.schedule, options);
      document.addEventListener('product-info:loaded', this.schedule, options);
      document.addEventListener('change', this.schedule, options);
      document.addEventListener('focusin', this.schedule, options);
      document.addEventListener('focusout', this.schedule, options);

      this.link.addEventListener('click', (event) => {
        if (!this.destination()) return;
        event.preventDefault();
        this.goToOptions();
      }, options);
      this.button.addEventListener('click', () => {
        const state = this.purchaseState();
        if (!state || state.busy) return;
        if (!state.direct || state.submit.disabled) {
          this.goToOptions();
          return;
        }
        // Use the original form, quantity, validation, errors and AJAX cart.
        // Optional product properties added by apps also require the full form.
        if (!state.form.checkValidity()) {
          const invalid = [...state.form.elements].find((input) => input.willValidate && !input.validity.valid);
          this.goToOptions(invalid);
          state.form.reportValidity();
          return;
        }
        state.form.requestSubmit(state.submit);
        this.update();
      }, options);

      this.observer = new MutationObserver((records) => {
        if (records.some((record) => !this.contains(record.target))) this.schedule();
      });
      this.observer.observe(document.body, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['open', 'class', 'hidden', 'disabled', 'aria-disabled', 'aria-modal']
      });
      this.update();
    }

    destination() {
      return document.querySelector('product-info[data-update-url="true"] [id^="ProductInfo-"]');
    }

    purchaseState() {
      const destination = this.destination();
      const productForm = destination?.querySelector('product-form');
      const form = productForm?.querySelector('form');
      const submit = form?.querySelector('.product-form__submit');
      if (!form || !submit) return null;
      const hasChoices = [...form.elements].some((input) => {
        const name = input.name || '';
        return !input.disabled && (name.startsWith('properties[') || name === 'selling_plan');
      });
      const direct = productForm.dataset.rtDirectPurchase === 'true'
        && !hasChoices
        && typeof productForm.onSubmitHandler === 'function'
        && typeof form.requestSubmit === 'function';
      const busy = submit.getAttribute('aria-disabled') === 'true' || submit.classList.contains('loading');
      return { destination, productForm, form, submit, direct, busy };
    }

    goToOptions(preferredTarget) {
      const destination = this.destination();
      if (!destination) return;
      const target = preferredTarget
        || destination.querySelector('variant-selects input:not([disabled]), variant-selects select:not([disabled])')
        || destination.querySelector('[data-rt-personalisation]:not([disabled])')
        || destination.querySelector('.product-form__submit:not([disabled]), input[name="quantity"]:not([disabled])');
      destination.classList.add('rt-purchase-destination');
      if (target) target.focus({ preventScroll: true });
      else {
        destination.setAttribute('tabindex', '-1');
        destination.focus({ preventScroll: true });
      }
      (target?.closest('.product-form__input') || destination).scrollIntoView({
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
        block: 'start'
      });
    }

    update() {
      const state = this.purchaseState();
      const controls = state?.destination.querySelector('.product-form__buttons');
      if (!state || !controls?.getClientRects().length) {
        this.hidden = true;
        return;
      }
      this.link.href = `#${state.destination.id}`;
      const direct = state.direct && !state.submit.disabled;
      this.link.hidden = direct;
      this.button.hidden = !direct;
      this.button.disabled = state.busy;
      this.button.setAttribute('aria-busy', String(state.busy));
      const buttonLabel = state.busy ? this.dataset.busyLabel : this.dataset.addLabel;
      if (this.button.textContent !== buttonLabel) this.button.textContent = buttonLabel;
      this.button.toggleAttribute('aria-haspopup', state.submit.hasAttribute('aria-haspopup'));
      if (state.submit.hasAttribute('aria-haspopup')) this.button.setAttribute('aria-haspopup', state.submit.getAttribute('aria-haspopup'));

      const price = state.destination.querySelector('[id^="price-"]');
      const priceText = price?.querySelector(price.querySelector('.price--on-sale') ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular')?.textContent.trim();
      if (this.price) {
        this.price.hidden = !priceText || price.classList.contains('hidden');
        if (priceText && this.price.textContent !== priceText) this.price.textContent = priceText;
      }
      const error = state.productForm.querySelector('.product-form__error-message-wrapper:not([hidden])');
      if (error && !this.hadError) this.goToOptions(state.submit);
      this.hadError = Boolean(error);

      const overlayOpen = [...document.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"], cart-drawer.active, menu-drawer details[open], details-modal details[open]')]
        .some((element) => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
      const editing = document.activeElement?.matches('input:not([type="hidden"]), textarea, select, [contenteditable="true"]');
      this.hidden = controls.getBoundingClientRect().bottom >= 0 || overlayOpen || editing;
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  });
}
